import {
    useMemo, useState,
} from 'react'
import noop from 'lodash/noop'
import {
    ForecastObjectResponse,
} from 'app/requests/Forecast/Forecast.types'

import useEnumValues from 'app/hooks/useEnumValues'
import {
    RequestConfig,
} from 'app/types/request.types'
import FormWithControls from 'app/shared-components/FormWithControls'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import {
    ProductType,
} from 'app/enums/label.enums'

import {
    ForecastDataFields,
    ForecastDataSpreadsheetColumnsConfig,
    ForecastSpreadsheetDataTypeEnum,
} from '../../Forecast.types'
import {
    dataToSpreadsheetColumnsMap, getKeyByMonthYear, parseForecastData,
} from '../../Forecast.utils'

import getFields from './ShortTermForecastForm.fields'
import useStyles from './ShortTermForecastForm.style'

const defaultRequiredRows = [
    ForecastSpreadsheetDataTypeEnum.Forecast,
    ForecastSpreadsheetDataTypeEnum.Actuals,
]

export default function ShortTermForecastForm({
    requestParam,
    onSuccess = noop,
    onCancel = noop,
    data,
    disabled = false,
    requiredRows = defaultRequiredRows,
}: Readonly<{
    onSuccess?: (...a: any[]) => void,
    onCancel?: (...a: any[]) => void,
    data: ForecastObjectResponse,
    requestParam?: RequestConfig,
    disabled?: boolean,
    requiredRows?: ForecastSpreadsheetDataTypeEnum[],
}>) {
    const datesHeader = useMemo(() => {
        return (data?.forecastsWithActuals || []).map(({
            month, year,
        }) => {
            return getKeyByMonthYear({
                month: month - 1, year,
            })
        })
    }, [data?.forecastsWithActuals])

    const tmpFields: ForecastDataSpreadsheetColumnsConfig = useMemo(() => {
        return Array.from(datesHeader).sort().map(dataToSpreadsheetColumnsMap({
            disabled: false,
        }))
    }, [datesHeader])

    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()
    const dataMap: Record<ForecastSpreadsheetDataTypeEnum, ForecastDataFields> = useMemo(() => {
        return {
            [ForecastSpreadsheetDataTypeEnum.Forecast]: 'units',
            [ForecastSpreadsheetDataTypeEnum.Actuals]: 'actuals',
        }
    }, [])

    const [value] = useState({
        ...data,
        spreadsheetData: requiredRows.map((dataType) => {
            return {
                dataType,
                ...(data?.forecastsWithActuals || []).reduce(
                    parseForecastData(dataMap[dataType]), [],
                ),
            }
        }),
    })

    const fields = useFieldsWithClassName(useMemo(() => {
        return getFields({
            getAllowedValues,
            productType: data?.productType,
            tmpFields,
            disabled,
            onSpreadsheetChange: noop,
            productTypeLabels: getAllowedValues(ProductType),
            showForecast: Boolean(tmpFields.length),
        })
    }, [
        tmpFields,
        getAllowedValues,
        data,
        disabled,
    ]), classes)

    return (
        <FormWithControls
            onSuccess={onSuccess}
            value={value}
            name="ForecastOngoingOpportunities"
            fields={fields}
            requestParams={requestParam}
            exists={Boolean(data?.id)}
            onCancel={onCancel}
            disabled={disabled}
            customLabelSave="Update"
        />
    )
}
